'use client';

import { produce } from 'immer';
import { FC, ReactNode } from 'react';

import { storeFactory } from './storeFactory';

export type DisplayMode = 'popular' | 'all';

interface FilterSidebarStoreStateData {
  initialOpenIndexes: number[];
  displayMode: DisplayMode;
  scrollPosition: number;
}

interface FilterSidebarStoreStateFunctions {
  setInitialOpenIndexes: (initialOpenIndexes: number[]) => void;
  toggleOpenIndex: (index: number, isOpen: boolean) => void;
  setDisplayMode: (displayMode: 'popular' | 'all') => void;
  setScrollPosition: (scrollPosition: number) => void;
}

const {
  store: filterSidebarStore,
  StoreProvider: FilterSidebarStoreProvider,
  useCreateStore: useCreateFilterSidebarStore,
} = storeFactory<FilterSidebarStoreStateData, FilterSidebarStoreStateFunctions>(
  () => ({
    initialOpenIndexes: [0],
    displayMode: 'popular',
    scrollPosition: 0,
  }),
  (set) => ({
    setInitialOpenIndexes: (initialOpenIndex: number[]) => {
      set((state) => {
        // Ensure no duplicate indexes exist before setting the state
        const uniqueIndexes = [...new Set(initialOpenIndex)];
        return produce(state, (draft) => {
          draft.initialOpenIndexes = uniqueIndexes;
        });
      });
    },
    toggleOpenIndex: (index: number, isOpen: boolean) => {
      set((state) => {
        const withoutIndex = state.initialOpenIndexes.filter((i) => i !== index);
        return produce(state, (draft) => {
          draft.initialOpenIndexes = isOpen ? [...withoutIndex, index] : withoutIndex;
        });
      });
    },
    setDisplayMode: (displayMode: 'popular' | 'all') => {
      set((state) =>
        produce(state, (draft) => {
          draft.displayMode = displayMode;
        }),
      );
    },
    setScrollPosition: (scrollPosition: number) => {
      set((state) =>
        produce(state, (draft) => {
          draft.scrollPosition = scrollPosition;
        }),
      );
    },
  }),
);

export { filterSidebarStore };

export const FilterSidebarStore: FC<{ children: ReactNode }> = ({ children }) => {
  const createFilterSidebarStore = useCreateFilterSidebarStore({});

  return <FilterSidebarStoreProvider createStore={createFilterSidebarStore}>{children}</FilterSidebarStoreProvider>;
};
